export type ThemeMode = 'light' | 'dark' | 'system'
export type ThemePresetId = 'current' | 'default' | 'workspace'
export type ThemeVariant = 'light' | 'dark'

export const THEME_MODE_STORAGE_KEY = 'theme-mode'
export const THEME_PRESET_STORAGE_KEY = 'theme-preset'

export const themeTokenNames = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'border',
  'input',
  'ring',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'radius',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
] as const

export type ThemeTokenName = (typeof themeTokenNames)[number]
export type ThemeTokenMap = Record<ThemeTokenName, string>
export type ThemePreset = Record<ThemeVariant, ThemeTokenMap>

export const editableThemeTokens = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'primary',
  'primary-foreground',
  'accent',
  'accent-foreground',
  'border',
  'ring',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
] as const

export type EditableThemeTokenName = (typeof editableThemeTokens)[number]
export type EditableThemeTokenMap = Record<EditableThemeTokenName, string>
export type EditableThemeTokensByVariant = Record<
  ThemeVariant,
  EditableThemeTokenMap
>

function pickEditableTokens(tokens: ThemeTokenMap): EditableThemeTokenMap {
  return Object.fromEntries(
    editableThemeTokens.map((token) => [token, tokens[token]]),
  ) as EditableThemeTokenMap
}

export const CAMEL_TO_KEBAB: Partial<Record<string, EditableThemeTokenName>> = {
  background: 'background',
  foreground: 'foreground',
  card: 'card',
  cardForeground: 'card-foreground',
  primary: 'primary',
  primaryForeground: 'primary-foreground',
  accent: 'accent',
  accentForeground: 'accent-foreground',
  border: 'border',
  ring: 'ring',
  sidebar: 'sidebar',
  sidebarForeground: 'sidebar-foreground',
  sidebarPrimary: 'sidebar-primary',
  sidebarPrimaryForeground: 'sidebar-primary-foreground',
  sidebarAccent: 'sidebar-accent',
  sidebarAccentForeground: 'sidebar-accent-foreground',
}

export type WorkspaceThemeTokensFromApi = {
  light: Record<string, string>
  dark: Record<string, string>
} | null

export function mapWorkspaceTokensToKebab(
  workspaceTokens: WorkspaceThemeTokensFromApi,
): EditableThemeTokensByVariant | null {
  if (!workspaceTokens) {
    return null
  }

  const result: EditableThemeTokensByVariant =
    {} as EditableThemeTokensByVariant

  for (const variant of ['light', 'dark'] as const) {
    const variantMap: EditableThemeTokenMap = {} as EditableThemeTokenMap
    const source = workspaceTokens[variant]

    for (const camelKey of Object.keys(source)) {
      const kebabKey = CAMEL_TO_KEBAB[camelKey]
      if (kebabKey) {
        variantMap[kebabKey] = source[camelKey]
      }
    }

    result[variant] = variantMap
  }

  return result
}

export const currentThemePreset: ThemePreset = {
  light: {
    background: 'oklch(0.99 0 0)',
    foreground: 'oklch(0.15 0 0)',
    card: 'oklch(1 0 0)',
    'card-foreground': 'oklch(0.15 0 0)',
    popover: 'oklch(1 0 0)',
    'popover-foreground': 'oklch(0.15 0 0)',
    primary: 'oklch(0.25 0 0)',
    'primary-foreground': 'oklch(0.99 0 0)',
    secondary: 'oklch(0.92 0 0)',
    'secondary-foreground': 'oklch(0.25 0 0)',
    muted: 'oklch(0.95 0 0)',
    'muted-foreground': 'oklch(0.5 0 0)',
    accent: 'oklch(0.9 0 0)',
    'accent-foreground': 'oklch(0.25 0 0)',
    destructive: 'oklch(0.61 0.22 25)',
    'destructive-foreground': 'oklch(0.98 0.01 20)',
    border: 'oklch(0.85 0 0)',
    input: 'oklch(0.88 0 0)',
    ring: 'oklch(0.35 0 0)',
    'chart-1': 'oklch(0.45 0.02 260)',
    'chart-2': 'oklch(0.5 0.04 250)',
    'chart-3': 'oklch(0.55 0.06 22)',
    'chart-4': 'oklch(0.6 0.08 85)',
    'chart-5': 'oklch(0.5 0.06 150)',
    radius: '0.625rem',
    sidebar: 'oklch(0.97 0 0)',
    'sidebar-foreground': 'oklch(0.15 0 0)',
    'sidebar-primary': 'oklch(0.25 0 0)',
    'sidebar-primary-foreground': 'oklch(0.99 0 0)',
    'sidebar-accent': 'oklch(0.92 0 0)',
    'sidebar-accent-foreground': 'oklch(0.25 0 0)',
    'sidebar-border': 'oklch(0.85 0 0)',
    'sidebar-ring': 'oklch(0.35 0 0)',
  },
  dark: {
    background: 'oklch(0.15 0 0)',
    foreground: 'oklch(0.95 0 0)',
    card: 'oklch(0.2 0 0)',
    'card-foreground': 'oklch(0.95 0 0)',
    popover: 'oklch(0.2 0 0)',
    'popover-foreground': 'oklch(0.95 0 0)',
    primary: 'oklch(0.92 0 0)',
    'primary-foreground': 'oklch(0.2 0 0)',
    secondary: 'oklch(0.25 0 0)',
    'secondary-foreground': 'oklch(0.9 0 0)',
    muted: 'oklch(0.28 0 0)',
    'muted-foreground': 'oklch(0.65 0 0)',
    accent: 'oklch(0.3 0 0)',
    'accent-foreground': 'oklch(0.92 0 0)',
    destructive: 'oklch(0.4 0.15 25)',
    'destructive-foreground': 'oklch(0.96 0.015 20)',
    border: 'oklch(0.28 0 0)',
    input: 'oklch(0.28 0 0)',
    ring: 'oklch(0.7 0 0)',
    'chart-1': 'oklch(0.55 0.02 260)',
    'chart-2': 'oklch(0.6 0.04 250)',
    'chart-3': 'oklch(0.6 0.08 22)',
    'chart-4': 'oklch(0.65 0.09 85)',
    'chart-5': 'oklch(0.55 0.06 150)',
    radius: '0.625rem',
    sidebar: 'oklch(0.18 0 0)',
    'sidebar-foreground': 'oklch(0.95 0 0)',
    'sidebar-primary': 'oklch(0.92 0 0)',
    'sidebar-primary-foreground': 'oklch(0.2 0 0)',
    'sidebar-accent': 'oklch(0.25 0 0)',
    'sidebar-accent-foreground': 'oklch(0.92 0 0)',
    'sidebar-border': 'oklch(0.28 0 0)',
    'sidebar-ring': 'oklch(0.7 0 0)',
  },
}

export const defaultThemePreset: ThemePreset = {
  light: {
    background: '#F4F7FB',
    foreground: '#0B172D',
    card: '#FFFFFF',
    'card-foreground': '#0B172D',
    popover: '#FFFFFF',
    'popover-foreground': '#0B172D',
    primary: '#0066D6',
    'primary-foreground': '#FFFFFF',
    secondary: '#E8EEF6',
    'secondary-foreground': '#0B172D',
    muted: '#E8EEF6',
    'muted-foreground': '#5A6B82',
    accent: '#DDF3FC',
    'accent-foreground': '#0B172D',
    destructive: '#C44335',
    'destructive-foreground': '#FFFFFF',
    border: '#B7C4D6',
    input: '#B7C4D6',
    ring: '#0066D6',
    'chart-1': '#0066D6',
    'chart-2': '#00849C',
    'chart-3': '#9A6D24',
    'chart-4': '#C44335',
    'chart-5': '#66758C',
    radius: '0.625rem',
    sidebar: '#EDF2F8',
    'sidebar-foreground': '#0B172D',
    'sidebar-primary': '#0066D6',
    'sidebar-primary-foreground': '#FFFFFF',
    'sidebar-accent': '#DDF3FC',
    'sidebar-accent-foreground': '#0B172D',
    'sidebar-border': '#B7C4D6',
    'sidebar-ring': '#0066D6',
  },
  dark: {
    background: '#0B172D',
    foreground: '#F4F7FB',
    card: '#16243B',
    'card-foreground': '#F4F7FB',
    popover: '#16243B',
    'popover-foreground': '#F4F7FB',
    primary: '#39C6F4',
    'primary-foreground': '#0B172D',
    secondary: '#20324D',
    'secondary-foreground': '#F4F7FB',
    muted: '#20324D',
    'muted-foreground': '#AAB8CC',
    accent: '#173D55',
    'accent-foreground': '#E5F7FD',
    destructive: '#FF8975',
    'destructive-foreground': '#0B172D',
    border: '#425571',
    input: '#425571',
    ring: '#39C6F4',
    'chart-1': '#4A98FF',
    'chart-2': '#39C6F4',
    'chart-3': '#F0CD82',
    'chart-4': '#FF8975',
    'chart-5': '#AAB8CC',
    radius: '0.625rem',
    sidebar: '#111E33',
    'sidebar-foreground': '#F4F7FB',
    'sidebar-primary': '#39C6F4',
    'sidebar-primary-foreground': '#0B172D',
    'sidebar-accent': '#173D55',
    'sidebar-accent-foreground': '#E5F7FD',
    'sidebar-border': '#425571',
    'sidebar-ring': '#39C6F4',
  },
}

export const themePresets: Record<ThemePresetId, ThemePreset> = {
  current: currentThemePreset,
  default: defaultThemePreset,
  workspace: defaultThemePreset,
}

export const currentEditableThemeTokens: EditableThemeTokensByVariant = {
  light: pickEditableTokens(currentThemePreset.light),
  dark: pickEditableTokens(currentThemePreset.dark),
}

export const defaultEditableThemeTokens: EditableThemeTokensByVariant = {
  light: pickEditableTokens(defaultThemePreset.light),
  dark: pickEditableTokens(defaultThemePreset.dark),
}

export function resolveThemePreset({
  preset,
  workspaceTokens,
}: {
  preset: ThemePresetId
  workspaceTokens: EditableThemeTokensByVariant | null
}): ThemePreset {
  if (preset === 'current') {
    return themePresets.current
  }

  if (preset === 'default') {
    return themePresets.default
  }

  if (!workspaceTokens) {
    return themePresets.default
  }

  const mergedLight = {
    ...themePresets.default.light,
    ...workspaceTokens.light,
  }
  const mergedDark = {
    ...themePresets.default.dark,
    ...workspaceTokens.dark,
  }

  return {
    light: mergedLight,
    dark: mergedDark,
  }
}

export { generateRandomThemeTokens } from './generate-random-theme-tokens'
